import React, { useState, useEffect } from 'react';
import {
  Plus, PlusCircle, Search, Filter, Package, Eye, Edit2, Trash2,
  UserPlus, Clock, AlertCircle, Download, FileText, Settings, FolderPlus, X, Camera
} from 'lucide-react';
import Modal from './Modal';
import { useLanguage } from '../contexts/LanguageContext';

interface Equipment {
  id: number;
  category_id: number;
  category_name: string;
  stock_code: string;
  brand: string;
  model: string;
  serial_number: string;
  purchase_price: number;
  purchase_date: string;
  total_quantity: number;
  available_quantity: number;
  minimum_stock: number;
  is_low_stock: boolean;
  status: 'available' | 'assigned' | 'maintenance' | 'retired';
  condition_status: 'new' | 'good' | 'fair' | 'poor' | 'broken';
  location_id: number;
  location_name: string;
  notes: string;
  photo_path: string;
  created_at: string;
  created_by_username: string;
}

interface Category {
  id: number;
  name: string;
  description: string;
}

interface Location {
  id: number;
  name: string;
}

interface User {
  id: number;
  name: string;
  email: string;
  role: string;
  department?: string;
  is_active?: number | boolean;
}

const EquipmentPage: React.FC = () => {
  const { t, language } = useLanguage();
  const [equipment, setEquipment] = useState<Equipment[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showQuickCategoryModal, setShowQuickCategoryModal] = useState(false);
  const [selectedEquipment, setSelectedEquipment] = useState<Equipment | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedPhoto, setSelectedPhoto] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string>('');
  const [photoUploading, setPhotoUploading] = useState(false);
  const [equipmentHistory, setEquipmentHistory] = useState<any[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [showAssignmentFormModal, setShowAssignmentFormModal] = useState(false);
  const [assignmentFormNotes, setAssignmentFormNotes] = useState('');
  const [assignmentFormExtras, setAssignmentFormExtras] = useState<string[]>([]);
  const [pendingAssignFormData, setPendingAssignFormData] = useState<typeof assignFormData | null>(null);
  const [showAddStockModal, setShowAddStockModal] = useState(false);
  const [assignFormData, setAssignFormData] = useState({
    user_id: '',
    assignment_type: 'temporary',
    quantity: '1',
    condition_on_assignment: 'good',
    notes: ''
  });
  const [addStockFormData, setAddStockFormData] = useState({
    quantity: '1',
    notes: ''
  });

  // Return form states
  const [showReturnFormModal, setShowReturnFormModal] = useState(false);
  const [selectedAssignmentForReturn, setSelectedAssignmentForReturn] = useState<any>(null);
  const [returnFormNotes, setReturnFormNotes] = useState('');
  const [returnFormPhotos, setReturnFormPhotos] = useState<string[]>([]);
  const [isCreatingReturnForm, setIsCreatingReturnForm] = useState(false);
  
  const [formData, setFormData] = useState({
    category_id: '',
    brand: '',
    model: '',
    serial_number: '',
    purchase_price: '',
    purchase_date: '',
    total_quantity: '',
    minimum_stock: '',
    location_id: '',
    notes: '',
    photo_path: ''
  });

  const [categoryFormData, setCategoryFormData] = useState({
    name: '',
    description: ''
  });
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [showEditCategoryModal, setShowEditCategoryModal] = useState(false);

  const statusLabels: Record<string, string> = {
    available: t('equipment.statusAvailable'),
    assigned: language === 'en' ? 'Assigned' : 'Zimmetli',
    maintenance: t('equipment.statusMaintenance'),
    retired: t('equipment.statusRetired')
  };

  const conditionLabels: Record<string, string> = {
    new: language === 'en' ? 'New' : 'Yeni',
    good: t('equipment.conditionGood'),
    fair: t('equipment.conditionFair'),
    poor: t('equipment.conditionPoor'),
    broken: language === 'en' ? 'Broken' : 'Bozuk'
  };

  const statusColors = {
    available: 'bg-green-100 text-green-800',
    assigned: 'bg-primary/20 text-primary-dark',
    maintenance: 'bg-yellow-100 text-yellow-800',
    retired: 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
  };

  useEffect(() => {
    fetchData();
  }, [currentPage, selectedCategory, selectedStatus, searchTerm]);

  useEffect(() => {
    if (showAssignModal) {
      fetchUsers();
      setUserSearchTerm('');
    }
  }, [showAssignModal]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: '20',
        ...(selectedCategory && { category_id: selectedCategory }),
        ...(selectedStatus && { status: selectedStatus }),
        ...(searchTerm && { search: searchTerm })
      });

      const [equipmentRes, categoriesRes, locationsRes] = await Promise.all([
        fetch(`/api/equipment?${params}`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
        }),
        fetch('/api/equipment/categories', {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
        }),
        fetch('/api/locations', {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
        })
      ]);

      if (equipmentRes.ok && categoriesRes.ok && locationsRes.ok) {
        const equipmentData = await equipmentRes.json();
        const categoriesData = await categoriesRes.json();
        const locationsData = await locationsRes.json();

        setEquipment(equipmentData.data);
        setCategories(categoriesData.data);
        setLocations(locationsData.data || []);
        setTotalPages(equipmentData.pagination?.pages || 1);
      }
    } catch (error) {
      console.error('Veri yükleme hatası:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await fetch('/api/users', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        const list: User[] = (data.data || data || []).filter((u: User) => Number(u.is_active) === 1);
        list.sort((a, b) => a.name.localeCompare(b.name, 'tr'));
        setUsers(list);
      }
    } catch (error) {
      console.error('Kullanıcılar yüklenemedi:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      // Upload photo first if selected
      let photoPath = formData.photo_path;
      if (selectedPhoto) {
        photoPath = await uploadPhoto() || '';
      }

      const submitData = { ...formData, photo_path: photoPath };

      const url = selectedEquipment
        ? `/api/equipment/${selectedEquipment.id}`
        : '/api/equipment';
      const method = selectedEquipment ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify(submitData)
      });

      if (response.ok) {
        setShowModal(false);
        setSelectedEquipment(null);
        setFormData({
          category_id: '', brand: '', model: '', serial_number: '',
          purchase_price: '', purchase_date: '', total_quantity: '',
          minimum_stock: '', location_id: '', notes: '', photo_path: ''
        });
        setSelectedPhoto(null);
        setPhotoPreview('');
        fetchData();

        window.dispatchEvent(new CustomEvent('showNotification', {
          detail: {
            type: 'success',
            message: selectedEquipment ? t('equipment.successUpdated') : t('equipment.successCreated')
          }
        }));
      } else {
        const errorData = await response.json();
        window.dispatchEvent(new CustomEvent('showNotification', {
          detail: {
            type: 'error',
            message: errorData.error || t('equipment.errSave')
          }
        }));
      }
    } catch (error) {
      console.error('Ekipman işlem hatası:', error);
      window.dispatchEvent(new CustomEvent('showNotification', {
        detail: {
          type: 'error',
          message: language === 'en' ? 'Connection error' : 'Bağlantı hatası'
        }
      }));
    }
  };

  const handlePhotoSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedPhoto(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setPhotoPreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadPhoto = async () => {
    if (!selectedPhoto) return null;

    const photoFormData = new FormData();
    photoFormData.append('photo', selectedPhoto);

    setPhotoUploading(true);
    try {
      const response = await fetch('/api/upload/equipment-photo', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: photoFormData
      });

      if (response.ok) {
        const result = await response.json();
        return result.data.path;
      } else {
        throw new Error('Fotoğraf yükleme başarısız');
      }
    } finally {
      setPhotoUploading(false);
    }
  };

  const doAssign = async (data: typeof assignFormData): Promise<number | null> => {
    if (!selectedEquipment) return null;
    const response = await fetch(`/api/equipment/${selectedEquipment.id}/assign`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
      },
      body: JSON.stringify({
        user_id: parseInt(data.user_id),
        assignment_type: data.assignment_type,
        quantity: parseInt(data.quantity),
        condition_on_assignment: data.condition_on_assignment,
        notes: data.notes
      })
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || t('equipment.errAssign'));
    }
    const result = await response.json();
    return result.data?.assignment_id ?? null;
  };

  const handleAssign = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedEquipment || !assignFormData.user_id) return;
    setPendingAssignFormData({ ...assignFormData });
    setAssignmentFormNotes('');
    setAssignmentFormExtras([]);
    setShowAssignModal(false);
    setShowAssignmentFormModal(true);
  };

  const confirmAssignmentWithForm = async () => {
    if (!selectedEquipment || !pendingAssignFormData) return;
    try {
      const assignmentId = await doAssign(pendingAssignFormData);
      if (!assignmentId) throw new Error('Zimmet ID alınamadı');

      const formRes = await fetch('/api/assignment-forms', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify({
          equipment_assignment_id: assignmentId,
          assigned_user_id: parseInt(pendingAssignFormData.user_id),
          notes: assignmentFormNotes,
          delivered_equipment: assignmentFormExtras.length > 0 ? assignmentFormExtras : undefined,
        })
      });
      if (!formRes.ok) throw new Error('Zimmet formu oluşturulamadı');
      const form = await formRes.json();

      setShowAssignmentFormModal(false);
      setPendingAssignFormData(null);
      setAssignFormData({ user_id: '', assignment_type: 'temporary', quantity: '1', condition_on_assignment: 'good', notes: '' });
      setSelectedEquipment(null);

      window.location.href = `/assignment-forms/${form.id}`;
    } catch (error: any) {
      window.dispatchEvent(new CustomEvent('showNotification', {
        detail: { type: 'error', message: error.message || t('equipment.errAssign') }
      }));
    }
  };

  const confirmAssignmentWithoutForm = async () => {
    if (!pendingAssignFormData) return;
    try {
      await doAssign(pendingAssignFormData);
      setShowAssignmentFormModal(false);
      setPendingAssignFormData(null);
      setAssignFormData({ user_id: '', assignment_type: 'temporary', quantity: '1', condition_on_assignment: 'good', notes: '' });
      setSelectedEquipment(null);
      fetchData();
      window.dispatchEvent(new CustomEvent('showNotification', {
        detail: { type: 'success', message: t('equipment.successAssigned') }
      }));
    } catch (error: any) {
      window.dispatchEvent(new CustomEvent('showNotification', {
        detail: { type: 'error', message: error.message || t('equipment.errAssign') }
      }));
    }
  };

  const handleReturnAssignment = (record: any) => {
    setSelectedAssignmentForReturn(record);
    setReturnFormNotes('');
    setReturnFormPhotos([]);
    setShowReturnFormModal(true);
  };

  const uploadReturnPhotos = async (formId: number, photos: string[]) => {
    if (photos.length === 0) return;
    const fd = new FormData();
    photos.forEach((dataUrl, i) => {
      const arr = dataUrl.split(',');
      const mime = arr[0].match(/:(.*?);/)![1];
      const bstr = atob(arr[1]);
      const u8arr = new Uint8Array(bstr.length);
      for (let n = 0; n < bstr.length; n++) u8arr[n] = bstr.charCodeAt(n);
      fd.append('photos', new Blob([u8arr], { type: mime }), `photo-${i + 1}.jpg`);
    });
    await fetch(`/api/assignment-forms/${formId}/return-photos`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` },
      body: fd
    });
  };

  const addReturnFormPhoto = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    const remaining = 4 - returnFormPhotos.length;
    const toProcess = Math.min(files.length, remaining);
    for (let i = 0; i < toProcess; i++) {
      const file = files[i];
      const reader = new FileReader();
      reader.onload = (ev) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const maxW = 1200, maxH = 1600;
          let { width, height } = img;
          if (width > maxW || height > maxH) {
            const ratio = Math.min(maxW / width, maxH / height);
            width = Math.round(width * ratio);
            height = Math.round(height * ratio);
          }
          canvas.width = width;
          canvas.height = height;
          canvas.getContext('2d')!.drawImage(img, 0, 0, width, height);
          const dataUrl = canvas.toDataURL('image/jpeg', 0.75);
          setReturnFormPhotos(prev => prev.length < 4 ? [...prev, dataUrl] : prev);
        };
        img.src = ev.target?.result as string;
      };
      reader.readAsDataURL(file);
    }
    e.target.value = '';
  };

  const handleReturnFormConfirm = async (withForm: boolean) => {
    if (!selectedAssignmentForReturn) return;
    setIsCreatingReturnForm(true);
    setShowReturnFormModal(false);

    try {
      let formId: number | null = null;

      if (withForm) {
        const now = new Date();
        const pad = (n: number) => String(n).padStart(2, '0');
        const mysqlDateTime = `${now.getFullYear()}-${pad(now.getMonth()+1)}-${pad(now.getDate())} ${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`;

        const formRes = await fetch('/api/assignment-forms', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` },
          body: JSON.stringify({
            equipment_assignment_id: selectedAssignmentForReturn.id,
            assigned_user_id: selectedAssignmentForReturn.user_id,
            assignment_date: mysqlDateTime,
            form_type: 'return',
            notes: returnFormNotes || 'Zimmet geri teslim işlemi'
          })
        });

        if (!formRes.ok) {
          const err = await formRes.json().catch(() => ({}));
          throw new Error(err.error || 'Zimmet iade tutanağı oluşturulamadı');
        }

        const formData = await formRes.json();
        formId = formData.id;

        if (returnFormPhotos.length > 0) {
          await uploadReturnPhotos(formId!, returnFormPhotos);
        }
      }

      // Ekipmanı geri al
      const returnRes = await fetch(`/api/equipment/assignments/${selectedAssignmentForReturn.id}/return`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` },
        body: JSON.stringify({ condition_on_return: 'good', notes: returnFormNotes })
      });

      if (!returnRes.ok) {
        const err = await returnRes.json().catch(() => ({}));
        throw new Error(err.error || 'Zimmet iadesi başarısız');
      }

      window.dispatchEvent(new CustomEvent('showNotification', {
        detail: {
          type: 'success',
          message: withForm
            ? 'Zimmet iade tutanağı oluşturuldu.'
            : t('equipment.successUnassigned')
        }
      }));

      setShowHistoryModal(false);
      setSelectedEquipment(null);
      setEquipmentHistory([]);

      if (withForm) {
        window.location.href = '/assignment-forms';
      } else {
        fetchData();
      }
    } catch (error: any) {
      window.dispatchEvent(new CustomEvent('showNotification', {
        detail: { type: 'error', message: error.message }
      }));
    } finally {
      setIsCreatingReturnForm(false);
      setSelectedAssignmentForReturn(null);
      setReturnFormNotes('');
      setReturnFormPhotos([]);
    }
  };

  const handleAddStock = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedEquipment) return;

    try {
      const response = await fetch(`/api/equipment-new/${selectedEquipment.id}/add-stock`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify({
          quantity: parseInt(addStockFormData.quantity),
          notes: addStockFormData.notes
        })
      });

      if (response.ok) {
        setShowAddStockModal(false);
        setAddStockFormData({ quantity: '1', notes: '' });
        fetchData();
        window.dispatchEvent(new CustomEvent('showNotification', {
          detail: { type: 'success', message: t('equipment.successStockAdded') }
        }));
      } else {
        const errorData = await response.json();
        window.dispatchEvent(new CustomEvent('showNotification', {
          detail: { type: 'error', message: errorData.error || t('equipment.errStockAdd') }
        }));
      }
    } catch (error) {
      console.error('Stok ekleme hatası:', error);
      window.dispatchEvent(new CustomEvent('showNotification', {
        detail: { type: 'error', message: 'Bağlantı hatası' }
      }));
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm(t('equipment.confirmDelete'))) {
      return;
    }

    try {
      const response = await fetch(`/api/equipment/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (response.ok) {
        fetchData();
        window.dispatchEvent(new CustomEvent('showNotification', {
          detail: {
            type: 'success',
            message: t('equipment.successDeleted')
          }
        }));
      } else {
        const errorData = await response.json();
        window.dispatchEvent(new CustomEvent('showNotification', {
          detail: {
            type: 'error',
            message: errorData.error || t('equipment.errDelete')
          }
        }));
      }
    } catch (error) {
      console.error('Silme hatası:', error);
      window.dispatchEvent(new CustomEvent('showNotification', {
        detail: {
          type: 'error',
          message: language === 'en' ? 'Connection error' : 'Bağlantı hatası'
        }
      }));
    }
  };

  const handleViewHistory = async (equipment: Equipment) => {
    try {
      setSelectedEquipment(equipment);
      const response = await fetch(`/api/equipment/${equipment.id}/history`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setEquipmentHistory(data.data || []);
        setShowHistoryModal(true);
      } else {
        window.dispatchEvent(new CustomEvent('showNotification', {
          detail: {
            type: 'error',
            message: language === 'en' ? 'Error loading history' : 'Geçmiş bilgiler yüklenirken hata oluştu'
          }
        }));
      }
    } catch (error) {
      console.error('Geçmiş bilgiler yükleme hatası:', error);
      window.dispatchEvent(new CustomEvent('showNotification', {
        detail: {
          type: 'error',
          message: language === 'en' ? 'Connection error' : 'Bağlantı hatası'
        }
      }));
    }
  };

  const generateReport = async () => {
    try {
      const response = await fetch('/api/equipment/report', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify({
          category_id: selectedCategory,
          status: selectedStatus,
          search: searchTerm
        })
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `ekipman_raporu_${new Date().toISOString().split('T')[0]}.pdf`;
        a.click();
      }
    } catch (error) {
      console.error('Rapor oluşturma hatası:', error);
    }
  };

  const handleCategorySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const response = await fetch('/api/equipment/categories', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify(categoryFormData)
      });

      if (response.ok) {
        setShowCategoryModal(false);
        setShowQuickCategoryModal(false);
        setCategoryFormData({ name: '', description: '' });
        fetchData(); // Kategorileri yeniden yükle
        
        window.dispatchEvent(new CustomEvent('showNotification', {
          detail: {
            type: 'success',
            message: language === 'en' ? 'Category added successfully' : 'Kategori başarıyla eklendi'
          }
        }));
      } else {
        const errorData = await response.json();
        window.dispatchEvent(new CustomEvent('showNotification', {
          detail: {
            type: 'error',
            title: language === 'en' ? 'Error' : 'Hata',
            message: errorData.message || errorData.error || (language === 'en' ? 'Error adding category' : 'Kategori eklenirken hata oluştu')
          }
        }));
      }
    } catch (error) {
      console.error('Kategori ekleme hatası:', error);
      window.dispatchEvent(new CustomEvent('showNotification', {
        detail: {
          type: 'error',
          message: language === 'en' ? 'Connection error' : 'Bağlantı hatası'
        }
      }));
    }
  };

  const handleEditCategory = (category: Category) => {
    setEditingCategory(category);
    setCategoryFormData({
      name: category.name,
      description: category.description || ''
    });
    setShowEditCategoryModal(true);
  };

  const handleUpdateCategory = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingCategory) return;

    try {
      const response = await fetch(`/api/equipment/categories/${editingCategory.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify(categoryFormData)
      });

      if (response.ok) {
        setShowEditCategoryModal(false);
        setEditingCategory(null);
        setCategoryFormData({ name: '', description: '' });
        fetchData(); // Kategorileri yeniden yükle
        
        window.dispatchEvent(new CustomEvent('showNotification', {
          detail: {
            type: 'success',
            title: language === 'en' ? 'Success' : 'Başarılı',
            message: language === 'en' ? 'Category updated successfully.' : 'Kategori başarıyla güncellendi.'
          }
        }));
      } else {
        const errorData = await response.json();
        window.dispatchEvent(new CustomEvent('showNotification', {
          detail: {
            type: 'error',
            title: language === 'en' ? 'Error' : 'Hata',
            message: errorData.message || (language === 'en' ? 'Error updating category.' : 'Kategori güncellenirken bir hata oluştu.')
          }
        }));
      }
    } catch (error) {
      console.error('Kategori güncelleme hatası:', error);
      window.dispatchEvent(new CustomEvent('showNotification', {
        detail: {
          type: 'error',
          title: language === 'en' ? 'Error' : 'Hata',
          message: language === 'en' ? 'Error updating category.' : 'Kategori güncellenirken bir hata oluştu.'
        }
      }));
    }
  };

  const handleDeleteCategory = async (categoryId: number) => {
    if (!confirm(language === 'en' ? 'Are you sure you want to delete this category?' : 'Bu kategoriyi silmek istediğinizden emin misiniz?')) {
      return;
    }

    try {
      const response = await fetch(`/api/equipment/categories/${categoryId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (response.ok) {
        fetchData(); // Kategorileri yeniden yükle
        
        window.dispatchEvent(new CustomEvent('showNotification', {
          detail: {
            type: 'success',
            title: language === 'en' ? 'Success' : 'Başarılı',
            message: language === 'en' ? 'Category deleted successfully.' : 'Kategori başarıyla silindi.'
          }
        }));
      } else {
        const errorData = await response.json();
        window.dispatchEvent(new CustomEvent('showNotification', {
          detail: {
            type: 'error',
            title: language === 'en' ? 'Error' : 'Hata',
            message: errorData.message || (language === 'en' ? 'Error deleting category.' : 'Kategori silinirken bir hata oluştu.')
          }
        }));
      }
    } catch (error) {
      console.error('Kategori silme hatası:', error);
      window.dispatchEvent(new CustomEvent('showNotification', {
        detail: {
          type: 'error',
          title: language === 'en' ? 'Error' : 'Hata',
          message: language === 'en' ? 'Error deleting category.' : 'Kategori silinirken bir hata oluştu.'
        }
      }));
    }
  };

  const generateStockReport = async () => {
    try {
      const response = await fetch('/api/equipment/stock-report', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        
        // CSV dosyası oluştur
        const csvContent = [
          [language === 'en' ? 'Equipment' : 'Ekipman', language === 'en' ? 'Category' : 'Kategori', language === 'en' ? 'Brand' : 'Marka', language === 'en' ? 'Model' : 'Model', language === 'en' ? 'Serial No' : 'Seri No', language === 'en' ? 'Status' : 'Durum', language === 'en' ? 'Location' : 'Lokasyon', language === 'en' ? 'Stock Status' : 'Stok Durumu'].join(','),
          ...data.data.map((item: any) => [
            `${item.brand} ${item.model}`,
            item.category_name,
            item.brand || '',
            item.model || '',
            item.serial_number || '',
            item.status,
            item.location_name || '',
            `${item.available_quantity}/${item.total_quantity}`
          ].join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `ekipman_durumu_${new Date().toISOString().split('T')[0]}.csv`;
        link.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Stok raporu oluşturma hatası:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-6 gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100">
            {t('equipment.title')}
          </h1>
          <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mt-1">
            {t('equipment.subtitle')}
          </p>
        </div>
        <div className="grid grid-cols-2 sm:flex sm:space-x-3 gap-2 sm:gap-0">
          <button
            onClick={() => setShowCategoryModal(true)}
            className="flex items-center justify-center px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm min-h-[44px]"
          >
            <Settings className="w-4 h-4 mr-1 sm:mr-2" />
            <span className="hidden sm:inline">{language === 'en' ? 'Categories' : 'Kategoriler'}</span>
            <span className="sm:hidden">{language === 'en' ? 'Categories' : 'Kategori'}</span>
          </button>
          <button
            onClick={generateStockReport}
            className="flex items-center justify-center px-3 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors text-sm min-h-[44px]"
          >
            <AlertCircle className="w-4 h-4 mr-1 sm:mr-2" />
            <span className="hidden sm:inline">{language === 'en' ? 'Stock Status' : 'Stok Durumu'}</span>
            <span className="sm:hidden">{language === 'en' ? 'Stock' : 'Stok'}</span>
          </button>
          <button
            onClick={generateReport}
            className="flex items-center justify-center px-3 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm min-h-[44px]"
          >
            <Download className="w-4 h-4 mr-1 sm:mr-2" />
            <span className="hidden sm:inline">{language === 'en' ? 'Get Report' : 'Rapor Al'}</span>
            <span className="sm:hidden">{language === 'en' ? 'Report' : 'Rapor'}</span>
          </button>
          <button
            onClick={() => setShowModal(true)}
            className="flex items-center justify-center px-3 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm min-h-[44px] col-span-2 sm:col-span-1"
          >
            <Plus className="w-4 h-4 mr-1 sm:mr-2" />
            {t('equipment.newEquipment')}
          </button>
        </div>
      </div>

      {/* Filtreler */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm mb-6 p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder={t('equipment.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-3 w-full border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-gray-100 text-base min-h-[44px]"
            />
          </div>
          
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-gray-100 text-base min-h-[44px]"
          >
            <option value="">{t('equipment.allCategories')}</option>
            {(categories || []).map(category => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>

          <select
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            className="px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-gray-100 text-base min-h-[44px]"
          >
            <option value="">{t('equipment.allStatuses')}</option>
            {Object.entries(statusLabels).map(([key, label]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>

          <button
            onClick={() => {
              setSearchTerm('');
              setSelectedCategory('');
              setSelectedStatus('');
              setCurrentPage(1);
            }}
            className="flex items-center justify-center px-4 py-3 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500 min-h-[44px]"
          >
            <Filter className="w-4 h-4 mr-2" />
            {language === 'en' ? 'Clear' : 'Temizle'}
          </button>
        </div>
      </div>

      {/* Ekipman Listesi */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" style={{minWidth: '800px'}}>
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-2 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider">
                  {language === 'en' ? 'Equipment' : 'Ekipman'}
                </th>
                <th className="px-2 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider hide-on-mobile">
                  {language === 'en' ? 'Category' : 'Kategori'}
                </th>
                <th className="px-2 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider hide-on-mobile">
                  {language === 'en' ? 'Serial No' : 'Seri No'}
                </th>
                <th className="px-2 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider">
                  {language === 'en' ? 'Status' : 'Durum'}
                </th>
                <th className="px-2 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider">
                  {language === 'en' ? 'Stock' : 'Stok Durumu'}
                </th>
                <th className="px-2 sm:px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider">
                  {language === 'en' ? 'Actions' : 'İşlemler'}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700 dark:divide-gray-600">
              {(equipment || []).map((item) => (
                <tr key={item.id} className="hover:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-700">
                  <td className="px-2 sm:px-6 py-3 whitespace-nowrap">
                    <div className="flex items-center">
                      {item.photo_path ? (
                        <img
                          src={item.photo_path}
                          alt={`${item.brand} ${item.model}`}
                          className="w-10 h-10 object-cover rounded-md mr-2 sm:mr-3 flex-shrink-0"
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display = 'none';
                            const pkg = document.createElement('div');
                            pkg.innerHTML = '<svg class="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"></path></svg>';
                            (e.target as HTMLImageElement).parentNode?.insertBefore(pkg, e.target as Node);
                          }}
                        />
                      ) : (
                        <Package className="w-10 h-10 text-gray-400 mr-2 sm:mr-3 flex-shrink-0" />
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="text-xs sm:text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                          {item.brand} {item.model}
                        </div>
                        <div className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 truncate">
                          {item.location_name || t('equipment.locationNotSet')}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 sm:hidden">
                          {item.category_name} • {item.serial_number || '-'}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-2 sm:px-6 py-3 whitespace-nowrap text-xs sm:text-sm text-gray-500 dark:text-gray-400 hide-on-mobile">
                    {item.category_name}
                  </td>
                  <td className="px-2 sm:px-6 py-3 whitespace-nowrap text-xs sm:text-sm text-gray-500 dark:text-gray-400 hide-on-mobile">
                    {item.serial_number || '-'}
                  </td>
                  <td className="px-2 sm:px-6 py-3 whitespace-nowrap">
                    <span className={`inline-flex px-1.5 py-0.5 text-xs font-semibold rounded-full ${statusColors[item.status]}`}>
                      {statusLabels[item.status]}
                    </span>
                  </td>
                  <td className="px-2 sm:px-6 py-3 whitespace-nowrap">
                    <div className="text-xs sm:text-sm text-gray-900 dark:text-gray-100">
                      <span className="font-semibold">{item.available_quantity}/{item.total_quantity}</span>
                      {item.is_low_stock && (
                        <div className="flex items-center mt-1">
                          <AlertCircle className="w-3 h-3 sm:w-4 sm:h-4 text-red-500 mr-1" />
                          <span className="text-xs text-red-600">{t('equipment.lowStock')}</span>
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-2 sm:px-6 py-3 whitespace-nowrap text-xs sm:text-sm font-medium">
                    <div className="flex items-center justify-end space-x-1 sm:space-x-2">
                      <button
                        onClick={() => handleViewHistory(item)}
                        className="text-primary hover:text-primary-dark dark:text-primary dark:hover:text-accent p-1 min-h-[32px] min-w-[32px] flex items-center justify-center"
                        title="Geçmiş"
                      >
                        <Eye className="w-3 h-3 sm:w-4 sm:h-4" />
                      </button>
                      
                      {item.available_quantity > 0 && (
                        <button
                          onClick={() => {
                            setSelectedEquipment(item);
                            setAssignFormData({ user_id: '', assignment_type: 'temporary', quantity: '1', condition_on_assignment: 'good', notes: '' });
                            setShowAssignModal(true);
                          }}
                          className="text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300 p-1 min-h-[32px] min-w-[32px] flex items-center justify-center"
                          title="Zimmetle"
                        >
                          <UserPlus className="w-3 h-3 sm:w-4 sm:h-4" />
                        </button>
                      )}

                      <button
                        onClick={() => {
                          setSelectedEquipment(item);
                          setAddStockFormData({ quantity: '1', notes: '' });
                          setShowAddStockModal(true);
                        }}
                        className="text-purple-600 hover:text-purple-900 dark:text-purple-400 dark:hover:text-purple-300 p-1 min-h-[32px] min-w-[32px] flex items-center justify-center"
                        title="Stok Ekle"
                      >
                        <PlusCircle className="w-3 h-3 sm:w-4 sm:h-4" />
                      </button>
                      
                      <button
                        onClick={() => {
                          setSelectedEquipment(item);
                          setFormData({
                            category_id: item.category_id.toString(),
                            brand: item.brand,
                            model: item.model,
                            serial_number: item.serial_number || '',
                            purchase_price: item.purchase_price?.toString() || '',
                            purchase_date: item.purchase_date || '',
                            total_quantity: item.total_quantity?.toString() || '1',
                            minimum_stock: item.minimum_stock?.toString() || '1',
                            location_id: item.location_id?.toString() || '',
                            notes: item.notes || '',
                            photo_path: item.photo_path || ''
                          });
                          // Mevcut fotoğrafı önizlemede göster
                          if (item.photo_path) {
                            setPhotoPreview(item.photo_path);
                          } else {
                            setPhotoPreview('');
                          }
                          setSelectedPhoto(null);
                          setShowModal(true);
                        }}
                        className="text-indigo-600 hover:text-indigo-900 dark:text-indigo-400 dark:hover:text-indigo-300 p-1 min-h-[32px] min-w-[32px] flex items-center justify-center"
                        title="Düzenle"
                      >
                        <Edit2 className="w-3 h-3 sm:w-4 sm:h-4" />
                      </button>

                      <button
                        onClick={() => handleDelete(item.id)}
                        className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 p-1 min-h-[32px] min-w-[32px] flex items-center justify-center"
                        title="Sil"
                      >
                        <Trash2 className="w-3 h-3 sm:w-4 sm:h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Sayfalama */}
        {totalPages > 1 && (
          <div className="bg-white dark:bg-gray-800 px-4 py-3 flex items-center justify-between border-t border-gray-200 dark:border-gray-700">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700 disabled:opacity-50"
              >
                Önceki
              </button>
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700 disabled:opacity-50"
              >
                Sonraki
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  Sayfa <span className="font-medium">{currentPage}</span> / <span className="font-medium">{totalPages}</span>
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                    const pageNum = i + 1;
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          currentPage === pageNum
                            ? 'z-10 bg-primary/10 border-primary text-primary'
                            : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Yeni Ekipman Modal */}
      <Modal isOpen={showModal} onClose={() => setShowModal(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
              {selectedEquipment ? t('equipment.editTitle') : t('equipment.newTitle')}
            </h3>
            
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {language === 'en' ? 'Category *' : 'Kategori *'}
                  </label>
                  <div className="flex space-x-2">
                    <select
                      value={formData.category_id}
                      onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
                      className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-gray-100"
                      required
                    >
                      <option value="">{t('equipment.selectCategory')}</option>
                      {(categories || []).map(category => (
                        <option key={category.id} value={category.id}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => setShowQuickCategoryModal(true)}
                      className="px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center"
                      title="Hızlı Kategori Ekle"
                    >
                      <FolderPlus className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {language === 'en' ? 'Brand *' : 'Marka *'}
                  </label>
                  <input
                    type="text"
                    value={formData.brand}
                    onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-gray-100"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {language === 'en' ? 'Model *' : 'Model *'}
                  </label>
                  <input
                    type="text"
                    value={formData.model}
                    onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-gray-100"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {language === 'en' ? 'Serial Number' : 'Seri Numarası'}
                  </label>
                  <input
                    type="text"
                    value={formData.serial_number}
                    onChange={(e) => setFormData({ ...formData, serial_number: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-gray-100"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {language === 'en' ? 'Purchase Price' : 'Alış Fiyatı'}
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.purchase_price}
                    onChange={(e) => setFormData({ ...formData, purchase_price: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-gray-100"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {language === 'en' ? 'Purchase Date' : 'Alış Tarihi'}
                  </label>
                  <input
                    type="date"
                    value={formData.purchase_date}
                    onChange={(e) => setFormData({ ...formData, purchase_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-gray-100"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {language === 'en' ? 'Total Quantity' : 'Toplam Adet'}
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={formData.total_quantity}
                    onChange={(e) => setFormData({ ...formData, total_quantity: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-gray-100"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {language === 'en' ? 'Minimum Stock Level' : 'Minimum Stok Uyarı Seviyesi'}
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={formData.minimum_stock}
                    onChange={(e) => setFormData({ ...formData, minimum_stock: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-gray-100"
                    required
                  />
                </div>


                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {language === 'en' ? 'Location' : 'Lokasyon'}
                  </label>
                  <select
                    value={formData.location_id}
                    onChange={(e) => setFormData({ ...formData, location_id: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-gray-100"
                  >
                    <option value="">{t('equipment.selectLocation')}</option>
                    {(locations || []).map(location => (
                      <option key={location.id} value={location.id}>
                        {location.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {language === 'en' ? 'Photo' : 'Fotoğraf'}
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoSelect}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-gray-100"
                />
                {photoPreview && (
                  <div className="mt-2 relative inline-block">
                    <img
                      src={photoPreview}
                      alt="Önizleme"
                      className="w-32 h-32 object-cover rounded-md border border-gray-300 dark:border-gray-600"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setPhotoPreview('');
                        setSelectedPhoto(null);
                        setFormData({ ...formData, photo_path: '' });
                      }}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                      title="Fotoğrafı Kaldır"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {language === 'en' ? 'Notes' : 'Notlar'}
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-gray-100"
                />
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setSelectedEquipment(null);
                    setFormData({
                      category_id: '', brand: '', model: '', serial_number: '',
                      purchase_price: '', purchase_date: '', total_quantity: '',
                      minimum_stock: '', location_id: '', notes: '', photo_path: ''
                    });
                    setSelectedPhoto(null);
                    setPhotoPreview('');
                  }}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-700"
                >
                  {t('equipment.cancel')}
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark"
                >
                  {selectedEquipment ? t('equipment.update') : t('equipment.save')}
                </button>
              </div>
            </form>
          </div>
        </Modal>

      {/* Kategori Yönetimi Modal */}
      <Modal isOpen={showCategoryModal} onClose={() => setShowCategoryModal(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                {language === 'en' ? 'Equipment Categories' : 'Ekipman Kategorileri'}
              </h3>
              <button
                onClick={() => setShowCategoryModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:text-gray-400 dark:hover:text-gray-300"
              >
                ×
              </button>
            </div>
            
            <div className="mb-4">
              <form onSubmit={handleCategorySubmit} className="flex space-x-2">
                <input
                  type="text"
                  value={categoryFormData.name}
                  onChange={(e) => setCategoryFormData({ ...categoryFormData, name: e.target.value })}
                  placeholder="Kategori adı"
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-gray-100"
                  required
                />
                <input
                  type="text"
                  value={categoryFormData.description}
                  onChange={(e) => setCategoryFormData({ ...categoryFormData, description: e.target.value })}
                  placeholder="Açıklama (opsiyonel)"
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-gray-100"
                />
                <button
                  type="submit"
                  className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark"
                >
                  Ekle
                </button>
              </form>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {categories.map((category) => (
                <div key={category.id} className="border border-gray-200 dark:border-gray-700 dark:border-gray-600 rounded-lg p-4 relative group">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-900 dark:text-gray-100">{category.name}</h4>
                      {category.description && (
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{category.description}</p>
                      )}
                    </div>
                    <div className="flex space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => handleEditCategory(category)}
                        className="p-1 text-primary hover:text-primary-dark hover:bg-primary/10 rounded"
                        title="Düzenle"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                      <button
                        onClick={() => handleDeleteCategory(category.id)}
                        className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded"
                        title="Sil"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Modal>

      {/* Kategori Düzenleme Modal */}
      <Modal isOpen={showEditCategoryModal} onClose={() => setShowEditCategoryModal(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md max-h-[85vh] overflow-y-auto">
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
              Kategori Düzenle
            </h3>
            
            <form onSubmit={handleUpdateCategory}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Kategori Adı *
                </label>
                <input
                  type="text"
                  value={categoryFormData.name}
                  onChange={(e) => setCategoryFormData({ ...categoryFormData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-gray-100"
                  required
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Açıklama
                </label>
                <textarea
                  value={categoryFormData.description}
                  onChange={(e) => setCategoryFormData({ ...categoryFormData, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-gray-100"
                />
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditCategoryModal(false);
                    setEditingCategory(null);
                    setCategoryFormData({ name: '', description: '' });
                  }}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-700"
                >
                  {t('equipment.cancel')}
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark"
                >
                  {t('equipment.update')}
                </button>
              </div>
            </form>
          </div>
        </Modal>

      {/* Zimmetleme Modal */}
      <Modal isOpen={showAssignModal} onClose={() => { setShowAssignModal(false); setSelectedEquipment(null); }}>
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-1">
            {language === 'en' ? 'Assign Equipment' : 'Ekipman Zimmetle'}
          </h3>
          {selectedEquipment && (
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              {selectedEquipment.brand} {selectedEquipment.model} — {language === 'en' ? 'Available stock:' : 'Mevcut stok:'} <span className="font-semibold text-green-600">{selectedEquipment.available_quantity}</span>
            </p>
          )}
          <form onSubmit={handleAssign}>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('equipment.historyUser')} *
                </label>
                <input
                  type="text"
                  placeholder={language === 'en' ? 'Search user...' : 'Kullanıcı ara...'}
                  value={userSearchTerm}
                  onChange={(e) => setUserSearchTerm(e.target.value)}
                  className="w-full px-3 py-2 mb-1 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-gray-100 text-sm"
                />
                <select
                  value={assignFormData.user_id}
                  onChange={(e) => setAssignFormData({ ...assignFormData, user_id: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-gray-100"
                  size={5}
                  required
                >
                  <option value="">{t('equipment.selectUser')}</option>
                  {users
                    .filter(u =>
                      u.name.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
                      (u.department && u.department.toLowerCase().includes(userSearchTerm.toLowerCase()))
                    )
                    .map(user => (
                      <option key={user.id} value={user.id}>
                        {user.name} {user.department ? `(${user.department})` : ''}
                      </option>
                    ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {language === 'en' ? 'Assignment Type *' : 'Zimmet Türü *'}
                </label>
                <select
                  value={assignFormData.assignment_type}
                  onChange={(e) => setAssignFormData({ ...assignFormData, assignment_type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-gray-100"
                  required
                >
                  <option value="temporary">{t('equipment.assignTypeTemp')}</option>
                  <option value="permanent">{t('equipment.assignTypePerm')}</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {language === 'en' ? 'Quantity *' : 'Adet *'}
                </label>
                <input
                  type="number"
                  min="1"
                  max={selectedEquipment?.available_quantity || 1}
                  value={assignFormData.quantity}
                  onChange={(e) => setAssignFormData({ ...assignFormData, quantity: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-gray-100"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {language === 'en' ? 'Equipment Condition' : 'Ekipman Durumu'}
                </label>
                <select
                  value={assignFormData.condition_on_assignment}
                  onChange={(e) => setAssignFormData({ ...assignFormData, condition_on_assignment: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-gray-100"
                >
                  <option value="new">{language === 'en' ? 'New' : 'Yeni'}</option>
                  <option value="good">{t('equipment.conditionGood')}</option>
                  <option value="fair">{t('equipment.conditionFair')}</option>
                  <option value="poor">{t('equipment.conditionPoor')}</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {language === 'en' ? 'Notes' : 'Notlar'}
                </label>
                <textarea
                  value={assignFormData.notes}
                  onChange={(e) => setAssignFormData({ ...assignFormData, notes: e.target.value })}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-gray-100"
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                type="button"
                onClick={() => { setShowAssignModal(false); setSelectedEquipment(null); }}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-700"
              >
                {t('equipment.cancel')}
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
              >
                {language === 'en' ? 'Assign' : 'Zimmetle'}
              </button>
            </div>
          </form>
        </div>
      </Modal>

      {/* Zimmet Formu Onay Modal */}
      <Modal isOpen={showAssignmentFormModal} onClose={() => { setShowAssignmentFormModal(false); setPendingAssignFormData(null); }}>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4">
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              {language === 'en' ? 'Create Assignment Form' : 'Zimmet Teslim Formu Oluştur'}
            </h3>
          </div>

          <div className="p-6">
            {selectedEquipment && pendingAssignFormData && (
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                <strong>{selectedEquipment.brand} {selectedEquipment.model}</strong>{' '}
                {language === 'en' ? 'will be assigned to' : 'ekipmanı'}{' '}
                <strong>{users.find(u => u.id.toString() === pendingAssignFormData.user_id)?.name}</strong>
                {language === 'en' ? '.' : ' kullanıcısına zimmetlenecek.'}
              </p>
            )}
            <p className="text-sm text-gray-700 dark:text-gray-300 mb-4">
              {language === 'en'
                ? 'Would you like to create an assignment form for this action?'
                : 'Bu zimmet işlemi için teslim formu oluşturmak istiyor musunuz?'}
            </p>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {language === 'en' ? 'Delivered Items (Optional)' : 'Teslim Edilen Ekipmanlar (Opsiyonel)'}
              </label>
              <div className="grid grid-cols-2 gap-2">
                {['Kutu', 'Kablo', 'Adaptör', 'Kılıf', 'Kullanım Kılavuzu', 'Garanti Belgesi', 'Şarj Aleti', 'Diğer Aksesuar'].map((item) => (
                  <label key={item} className="flex items-center gap-2 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={assignmentFormExtras.includes(item)}
                      onChange={(e) => {
                        setAssignmentFormExtras(prev =>
                          e.target.checked ? [...prev, item] : prev.filter(x => x !== item)
                        );
                      }}
                      className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300 group-hover:text-primary transition-colors">
                      {item}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {language === 'en' ? 'Notes (Optional)' : 'Notlar (Opsiyonel)'}
              </label>
              <textarea
                value={assignmentFormNotes}
                onChange={(e) => setAssignmentFormNotes(e.target.value)}
                placeholder={language === 'en' ? 'Special conditions, etc.' : 'Özel durumlar vb.'}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-gray-100"
                rows={2}
              />
            </div>
          </div>

          <div className="p-6 border-t border-gray-200 dark:border-gray-700">
            <div className="flex flex-col space-y-3">
              <div className="flex space-x-3">
                <button
                  onClick={confirmAssignmentWithForm}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors flex items-center justify-center gap-2"
                >
                  <FileText className="w-4 h-4" />
                  {language === 'en' ? 'Assign with Form' : 'Zimmet Formu ile Ata'}
                </button>
                <button
                  onClick={confirmAssignmentWithoutForm}
                  className="flex-1 px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark transition-colors flex items-center justify-center gap-2"
                >
                  <UserPlus className="w-4 h-4" />
                  {language === 'en' ? 'Assign without Form' : 'Form Olmadan Ata'}
                </button>
              </div>
              <button
                onClick={() => { setShowAssignmentFormModal(false); setPendingAssignFormData(null); setShowAssignModal(true); }}
                className="w-full px-4 py-2 text-gray-600 dark:text-gray-400 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700 transition-colors"
              >
                {t('equipment.cancel')}
              </button>
            </div>
          </div>
        </div>
      </Modal>

      {/* Zimmet Geçmişi Modal */}
      <Modal isOpen={showHistoryModal} onClose={() => { setShowHistoryModal(false); setSelectedEquipment(null); setEquipmentHistory([]); }}>
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                {language === 'en' ? 'Assignment History' : 'Zimmet Geçmişi'}
              </h3>
              {selectedEquipment && (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {selectedEquipment.brand} {selectedEquipment.model}
                </p>
              )}
            </div>
            <button
              onClick={() => { setShowHistoryModal(false); setSelectedEquipment(null); setEquipmentHistory([]); }}
              className="text-gray-400 hover:text-gray-600 dark:text-gray-400 dark:hover:text-gray-300"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {equipmentHistory.length === 0 ? (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              <Clock className="w-12 h-12 mx-auto mb-2 opacity-30" />
              <p>{t('equipment.noHistory')}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase">{t('equipment.historyUser')}</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase">{t('equipment.historyType')}</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase">{language === 'en' ? 'Qty' : 'Adet'}</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase">{language === 'en' ? 'Assign Date' : 'Zimmet Tarihi'}</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase">{t('equipment.historyReturnDate')}</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase">{language === 'en' ? 'Status' : 'Durum'}</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase">{t('equipment.historyAction')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700 dark:divide-gray-600">
                  {equipmentHistory.map((record: any) => (
                    <tr key={record.id} className="hover:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-700">
                      <td className="px-3 py-2 text-gray-900 dark:text-gray-100">{record.user_name || '-'}</td>
                      <td className="px-3 py-2 text-gray-500 dark:text-gray-400">
                        {record.assignment_type === 'temporary' ? t('equipment.assignTypeTemp') : t('equipment.assignTypePerm')}
                      </td>
                      <td className="px-3 py-2 text-gray-500 dark:text-gray-400">{record.quantity || 1}</td>
                      <td className="px-3 py-2 text-gray-500 dark:text-gray-400">
                        {record.assigned_date ? new Date(record.assigned_date).toLocaleDateString(language === 'en' ? 'en-US' : 'tr-TR') : '-'}
                      </td>
                      <td className="px-3 py-2 text-gray-500 dark:text-gray-400">
                        {record.return_date ? new Date(record.return_date).toLocaleDateString(language === 'en' ? 'en-US' : 'tr-TR') : '-'}
                      </td>
                      <td className="px-3 py-2">
                        <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${
                          record.status === 'active'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-600 dark:text-gray-400 dark:bg-gray-700 dark:text-gray-300'
                        }`}>
                          {record.status === 'active' ? t('equipment.statusActive') : t('equipment.statusReturned')}
                        </span>
                      </td>
                      <td className="px-3 py-2">
                        {record.status === 'active' && (
                          <button
                            onClick={() => handleReturnAssignment(record)}
                            className="inline-flex items-center px-2 py-1 text-xs font-medium text-red-600 hover:text-red-800 hover:bg-red-50 dark:hover:bg-red-900/20 rounded border border-red-200 dark:border-red-800 transition-colors"
                            title="Zimmeti geri kaldır"
                          >
                            <X className="w-3 h-3 mr-1" />
                            {language === 'en' ? 'Return' : 'Geri Kaldır'}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Modal>

      {/* Zimmet Geri Teslim Tutanağı Modal */}
      <Modal isOpen={showReturnFormModal} onClose={() => { setShowReturnFormModal(false); setSelectedAssignmentForReturn(null); setReturnFormNotes(''); setReturnFormPhotos([]); }}>
        {selectedAssignmentForReturn && (
          <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-md max-h-[85vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Zimmet Geri Teslim Tutanağı</h2>
                <button
                  onClick={() => { setShowReturnFormModal(false); setSelectedAssignmentForReturn(null); setReturnFormNotes(''); setReturnFormPhotos([]); }}
                  className="text-gray-400 hover:text-gray-600 dark:text-gray-400"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-4">
              <div className="bg-primary/10 p-4 rounded-lg">
                <div className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-primary flex-shrink-0" />
                  <div>
                    <div className="font-medium text-primary-dark">Zimmet Geri Teslim Tutanağı oluşturulsun mu?</div>
                    <div className="text-sm text-primary mt-1">Tutanak ile kullanıcıya dijital imza için onay e-postası gönderilecektir.</div>
                  </div>
                </div>
              </div>

              {selectedEquipment && (
                <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg text-sm space-y-1">
                  <div><span className="font-medium text-gray-600 dark:text-gray-400">Aksesuar:</span> <span className="text-gray-900 dark:text-gray-100">{selectedEquipment.brand} {selectedEquipment.model}</span></div>
                  <div><span className="font-medium text-gray-600 dark:text-gray-400">Teslim Eden:</span> <span className="text-gray-900 dark:text-gray-100">{selectedAssignmentForReturn.user_name}</span></div>
                  <div><span className="font-medium text-gray-600 dark:text-gray-400">Teslim Alan:</span> <span className="text-gray-900 dark:text-gray-100">{JSON.parse(localStorage.getItem('user') || '{}').name}</span></div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Geri Teslim Notları (İsteğe Bağlı)
                </label>
                <textarea
                  value={returnFormNotes}
                  onChange={(e) => setReturnFormNotes(e.target.value)}
                  placeholder="Aksesuar durumu, geri teslim sebebi vb. notlar ekleyebilirsiniz..."
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-primary focus:border-primary dark:bg-gray-700 dark:text-gray-100 text-sm"
                  rows={2}
                />
              </div>

              {/* Aksesuar Fotoğrafları */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Fotoğraf (En fazla 4 adet)
                </label>
                <div className="grid grid-cols-4 gap-2">
                  {returnFormPhotos.map((photo, idx) => (
                    <div key={idx} className="relative aspect-square">
                      <img src={photo} alt={`Fotoğraf ${idx + 1}`} className="w-full h-full object-cover rounded-md border border-gray-300 dark:border-gray-600" />
                      <button
                        onClick={() => setReturnFormPhotos(prev => prev.filter((_, i) => i !== idx))}
                        className="absolute top-0.5 right-0.5 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center hover:bg-red-600"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                  {returnFormPhotos.length < 4 && (
                    <label className="aspect-square border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-md flex flex-col items-center justify-center cursor-pointer hover:border-primary hover:bg-primary/10 transition-colors">
                      <Camera className="w-5 h-5 text-gray-400 mb-1" />
                      <span className="text-xs text-gray-400">Ekle</span>
                      <input type="file" accept="image/*" capture="environment" multiple className="hidden" onChange={addReturnFormPhoto} />
                    </label>
                  )}
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-3">
              <button
                onClick={() => { setShowReturnFormModal(false); setSelectedAssignmentForReturn(null); setReturnFormNotes(''); setReturnFormPhotos([]); }}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                İptal
              </button>
              <button
                onClick={() => handleReturnFormConfirm(false)}
                disabled={isCreatingReturnForm}
                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors disabled:opacity-50"
              >
                Tutanaksız Kaldır
              </button>
              <button
                onClick={() => handleReturnFormConfirm(true)}
                disabled={isCreatingReturnForm}
                className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark transition-colors flex items-center gap-1 disabled:opacity-50"
              >
                <FileText className="w-4 h-4" />
                Tutanak ile Kaldır
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Stok Ekleme Modal */}
      <Modal isOpen={showAddStockModal} onClose={() => { setShowAddStockModal(false); setSelectedEquipment(null); }}>
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md max-h-[85vh] overflow-y-auto">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-1">
            Stok Ekle
          </h3>
          {selectedEquipment && (
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              {selectedEquipment.brand} {selectedEquipment.model} — Mevcut: <span className="font-semibold">{selectedEquipment.available_quantity}/{selectedEquipment.total_quantity}</span>
            </p>
          )}
          <form onSubmit={handleAddStock}>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Eklenecek Adet *
                </label>
                <input
                  type="number"
                  min="1"
                  value={addStockFormData.quantity}
                  onChange={(e) => setAddStockFormData({ ...addStockFormData, quantity: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-gray-100"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Notlar
                </label>
                <textarea
                  value={addStockFormData.notes}
                  onChange={(e) => setAddStockFormData({ ...addStockFormData, notes: e.target.value })}
                  rows={2}
                  placeholder="Stok ekleme nedeni..."
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-gray-100"
                />
              </div>
            </div>
            <div className="flex justify-end space-x-3 mt-6">
              <button
                type="button"
                onClick={() => { setShowAddStockModal(false); setSelectedEquipment(null); }}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-700"
              >
                İptal
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700"
              >
                Stok Ekle
              </button>
            </div>
          </form>
        </div>
      </Modal>

      {/* Hızlı Kategori Ekleme Modal */}
      <Modal isOpen={showQuickCategoryModal} onClose={() => setShowQuickCategoryModal(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md max-h-[85vh] overflow-y-auto">
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
              Hızlı Kategori Ekle
            </h3>
            
            <form onSubmit={handleCategorySubmit}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Kategori Adı *
                </label>
                <input
                  type="text"
                  value={categoryFormData.name}
                  onChange={(e) => setCategoryFormData({ ...categoryFormData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-gray-100"
                  required
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Açıklama
                </label>
                <textarea
                  value={categoryFormData.description}
                  onChange={(e) => setCategoryFormData({ ...categoryFormData, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-gray-100"
                />
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowQuickCategoryModal(false);
                    setCategoryFormData({ name: '', description: '' });
                  }}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-700"
                >
                  İptal
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark"
                >
                  Ekle
                </button>
              </div>
            </form>
          </div>
        </Modal>
    </div>
  );
};

export default EquipmentPage;